'use client';

import { useTranslations } from '@dealerportal/i18n/client';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import classNames from 'classnames';
import { type FC, useEffect, useRef, useState } from 'react';

interface SortingProps {
  onSortChange: (sort: string) => void;
  className?: string;
  tags?: string[];
  hasPricing?: boolean;
}

export const SortPopover: FC<SortingProps> = ({
  onSortChange,
  className,
  tags,
  hasPricing,
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('products');
  const tCommon = useTranslations('common');

  const baseSortOptions = [
    { label: t('sortRecommended'), value: 'recommended' },
    { label: t('sortBrandAsc'), value: 'brandAsc' },
    { label: t('sortBrandDesc'), value: 'brandDesc' },
    { label: t('sortLineAsc'), value: 'lineAsc' },
    { label: t('sortLineDesc'), value: 'lineDesc' },
  ];
  const tagSortOptions =
    tags?.map((tag) => {
      const formattedTag = tag
        .split('_')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');

      return {
        label: formattedTag,
        value: `tag_${tag}`,
      };
    }) || [];
  const pricingSortOptions = hasPricing
    ? [
        { label: t('sortPriceHigh'), value: 'priceDesc' },
        { label: t('sortPriceLow'), value: 'priceAsc' },
      ]
    : [];

  const sortOptions = [
    ...baseSortOptions,
    ...tagSortOptions,
    ...pricingSortOptions,
  ];
  const selectedSort =
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('sort')) ||
    'recommended';
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleSortChange = (sort: string) => {
    onSortChange(sort);
    setDropdownOpen(false);
  };

  return (
    <div className={classNames('ui-relative', className)} ref={dropdownRef}>
      <BetterButton
        btnStyle="neutral"
        attributes={{
          onClick: () => setDropdownOpen(!isDropdownOpen),
          'aria-haspopup': 'listbox',
          'aria-expanded': `${isDropdownOpen}`,
        }}
        replaceClass={{
          'ui-h-12': 'ui-h-10',
          '!ui-px-6': '!ui-px-4',
          'ui-border-solid ui-border': 'ui-border-0',
          'ui-rounded-md': 'ui-rounded-none',
        }}
        labelClassName="ui-text-sm ui-font-semibold"
        label={tCommon('sort')}
        icon={
          <svg
            viewBox="0 0 14 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="ui-w-4"
          >
            <path
              d="M0 1C0 0.446875 0 0 1 0H13C13.5531 0 14 0.446875 14 1C14 1.55313 13.5531 2 13 2H1C0.446875 2 0 1.55313 0 1ZM0 6C0 5.44688 0.446875 5 1 5H9C9.55313 5 10 5.44688 10 6C10 6.55312 9.55313 7 9 7H1C0.446875 7 0 6.55312 0 6ZM6 11C6 11.5531 5.55313 12 5 12H1C0.446875 12 0 11.5531 0 11C0 10.4469 0.446875 10 1 10H5C5.55313 10 6 10.4469 6 11Z"
              fill="#111827"
            />
          </svg>
        }
      />

      {isDropdownOpen && (
        <ul className="ui-w-max ui-absolute ui-top-full ui-right-0 ui-m-0 ui-mt-1 ui-py-2 ui-z-50 ui-bg-white ui-border ui-border-gray-300 ui-shadow-lg ui-list-none ui-text-sm ui-font-semibold ui-rounded">
          {sortOptions.map((option) => (
            <li
              key={option.value}
              aria-selected={option.value === selectedSort}
              className={classNames(
                'ui-flex ui-ui-items-center ui-gap-2 ui-pl-4 ui-pr-6 ui-py-1 ui-cursor-pointer ui-text-left hover:ui-bg-gray-200',
                {
                  'ui-text-blue-600 ui-font-semibold':
                    option.value === selectedSort,
                }
              )}
              onClick={() => handleSortChange(option.value)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`ui-w-4 ${option.value === selectedSort ? 'ui-visible' : 'ui-invisible'}`}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
